// src/config/resourceConfig.ts
import React from 'react';
import {
  PeopleRounded as UserIcon,
  BusinessRounded as GroupIcon,
  HotelRounded as HotelIcon,
  DevicesRounded as DeviceIcon,
  NotificationsRounded as AlertIcon,
  ScienceRounded as SimulatorIcon,
  ReportGmailerrorredRounded as ErrorIcon,
  FormatListBulletedRounded as OperationIcon,
  BugReportRounded as ErrorDefinitionIcon,
  AdminPanelSettingsRounded as AdminOptionsIcon,
  ArticleRounded as LogsIcon,
  TodayRounded as ArrivalsIcon,
  ReceiptLongRounded as ReservationsIcon,
  InsightsRounded as SummaryIcon,
  AltRouteRounded as ChannelsIcon,
  ScheduleRounded as DurationsIcon,
  PaymentsRounded as PaymentsIcon,
  ApartmentRounded as HotelsOverviewIcon,
  AssessmentRounded as OperacionIcon,
  MonitorRounded as LiveBoardIcon,
  AccountBalanceRounded as ReconciliationIcon,
  MonitorHeartRounded as ControlIcon,
  SignalCellularAltRounded as StatusPageIcon,
  LeaderboardRounded as BenchmarkIcon,
  TuneRounded as FitIcon,
  SentimentSatisfiedAltRounded as SatisfactionIcon,
  VpnKeyRounded as DispensersIcon,
} from '@mui/icons-material';

// Import feature components
import { ArrivalsPage } from '@/features/arrivals';
import { ReservationsPage } from '@/features/reservations';
import { SummaryPage } from '@/features/summary';
import { ChannelsPage } from '@/features/channels';
import { DispensersPage } from '@/features/dispensers';
import { DurationsPage } from '@/features/durations';
import { PaymentsPage } from '@/features/payments';
import { HotelsOverviewPage } from '@/features/hotels-overview';
import { OperacionPage } from '@/features/operacion';
import { SatisfactionPage } from '@/features/satisfaction';
import { LiveBoardPage } from '@/features/live-board';
import { GroupList, GroupCreate, GroupEdit } from '@/features/groups';
import { HotelList, HotelCreate, HotelEdit } from '@/features/hotels';
import { UserList, UserCreate, UserEdit } from '@/features/users';
import { DeviceList, DeviceCreate, DeviceEdit } from '@/features/devices';
import { EventList } from '@/features/events';
import { ReconciliationPage } from '@/features/reconciliation';
import { ControlPage } from '@/features/control';
import { StatusPage } from '@/features/status';
import { BenchmarkPage } from '@/features/benchmark';
import { FitPage } from '@/features/fit';

import { AlertList, AlertShow } from '@/features/alerts';

import { getCurrentCapabilities, getCurrentUser } from '@/utils/auth';
import { canExportWith } from '@/lib/exportPermission';
import type { UserRole } from '@/types/models';
import ErrorMonitor from '@/features/error-monitor/components/ErrorMonitor';
import { ErrorDefinitionList } from '@/features/error-definitions';
import { AdminOptionsPage } from '@/features/admin-options';
import { LogsPage } from '@/features/logs';

// Resource configuration interface
interface ResourcePermissions {
  access: UserRole[];
  list: UserRole[];
  create: UserRole[];
  edit: UserRole[];
  delete: UserRole[];
  show: UserRole[];
  export?: UserRole[];
}

interface ResourceComponents {
  list?: React.ComponentType<any>;
  create?: React.ComponentType<any>;
  edit?: React.ComponentType<any>;
  show?: React.ComponentType<any>;
}

interface ResourceConfig {
  name: string;
  recordRepresentation?: string;
  route: string;
  label: string;
  icon?: React.ComponentType<any>;
  permissions: ResourcePermissions;
  components: ResourceComponents;
  showInMenu: boolean;
  menuOrder: number;
}


export const resourcesConfig: Record<string, ResourceConfig> = {
  // ============================================
  // DIRECCIÓN — Resumen / Canales / Tiempos / Cobros / Hoteles
  // (mockups 05/06/07/08/09, wave 3). Retires the old Dashboard (route '/').
  // Dirección only (Decision 7): Receptionist has no analytics access.
  // ============================================
  summary: {
    name: 'summary',
    route: '/summary',
    label: 'resources.summary.name',
    icon: SummaryIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
    },
    components: { list: SummaryPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 1,
  },

  channels: {
    name: 'channels',
    route: '/channels',
    label: 'resources.channelsAnalytics.name',
    icon: ChannelsIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
    },
    components: { list: ChannelsPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 2,
  },

  // «Dispensadores» — the production dashboard's dispenser block (keys
  // delivered, success rate, dispensers with activity, daily trend), as a
  // Dirección screen. Every customer role saw that block, Receptionist
  // included, so all of them keep it; the rail offers it only when the
  // user's hotels have a dispenser (useHasDispensers).
  dispensers: {
    name: 'dispensers',
    route: '/dispensers',
    label: 'resources.dispensers.name',
    icon: DispensersIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: [],
    },
    components: { list: DispensersPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 2,
  },

  durations: {
    name: 'durations',
    route: '/durations',
    label: 'resources.durations.name',
    icon: DurationsIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
    },
    components: { list: DurationsPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 3,
  },

  payments: {
    name: 'payments',
    route: '/payments',
    label: 'resources.payments.name',
    icon: PaymentsIcon,
    permissions: {
      // Not visible to Receptionist (Decision 7: no money totals) nor Viewer's
      // export (Decision 9) — access itself follows the same Dirección set.
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
    },
    components: { list: PaymentsPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 4,
  },

  'hotels-overview': {
    name: 'hotels-overview',
    route: '/hotels-overview',
    label: 'resources.hotelsOverview.name',
    icon: HotelsOverviewIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
    },
    components: { list: HotelsOverviewPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 5,
  },

  // ============================================
  // OPERACIÓN — Insight v2 (w7.u2, CF S6 "GM weekly page"). Scoped to one
  // property per request, unlike the rest of Dirección — see PropertySelector.
  // ============================================
  operacion: {
    name: 'operacion',
    route: '/operacion',
    label: 'resources.operacion.name',
    icon: OperacionIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
    },
    components: { list: OperacionPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 6,
  },

  // ============================================
  // SATISFACCIÓN — Insight v2: the guest satisfaction survey (overall and
  // ease scores, comments) for one property. Same roles as Operación.
  // ============================================
  satisfaction: {
    name: 'satisfaction',
    route: '/satisfaction',
    label: 'resources.satisfaction.name',
    icon: SatisfactionIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: [],
    },
    components: { list: SatisfactionPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 7,
  },

  // ============================================
  // LIVE BOARD - Insight v2 (w7.u3, CF S6 "Reception board"). Scoped to one
  // property per request, no period picker, polls every 15 s. Receptionist
  // gets the same access as Llegadas/Reservas (arrivals/reservations) -
  // this is its own screen, per commercial-fit.md S3's "free, per hotel".
  // ============================================
  'live-board': {
    name: 'live-board',
    route: '/live-board',
    label: 'resources.liveBoard.name',
    icon: LiveBoardIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: [],
    },
    components: { list: LiveBoardPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 8,
  },

  // Dispenser analytics live only inside the main dashboard (equipment-profile
  // composition in Dashboard.tsx); the standalone resource/route was orphaned
  // (no component, hidden from menu) and has been removed.

  // ============================================
  // ARRIVALS - «Llegadas de hoy» (mockup 01, hotel-operations redesign)
  // ============================================
  arrivals: {
    name: 'arrivals',
    route: '/arrivals',
    label: 'resources.arrivals.name',
    icon: ArrivalsIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'], // Receptionist/Viewer cannot export (Decision 7)
    },
    components: {
      list: ArrivalsPage,
      create: undefined,
      edit: undefined,
      show: undefined,
    },
    showInMenu: true,
    menuOrder: 6,
  },

  // ============================================
  // RESERVATIONS - «Reservas» (mockup 03, hotel-operations redesign)
  // ============================================
  reservations: {
    name: 'reservations',
    recordRepresentation: 'reservationNumber',
    route: '/reservations',
    label: 'resources.reservations.name',
    icon: ReservationsIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'], // Viewer/Receptionist cannot export (Decision 7)
    },
    components: {
      list: ReservationsPage,
      create: undefined,
      edit: undefined,
      show: undefined,
    },
    showInMenu: true,
    menuOrder: 7,
  },

  // Actividad (activity-monitor) retired as a customer-facing resource/route
  // (Decision 11): `/activity-monitor` redirects to `/arrivals` (App.tsx).
  // The underlying `LiveActivityMonitor` component is NOT deleted — the
  // pre-existing public panel-link feature (`src/panel/PanelScreen.tsx`)
  // still renders it for a bound "activity" panel type, unrelated to this
  // redesign (same caution as wave 2's "second panel concept" note).

  // ============================================
  // ERROR MONITOR - Real-time error monitoring
  // Accessible to SuperAdmin, GroupAdmin and HotelAdmin
  // ============================================
  'error-monitor': {
    name: 'error-monitor',
    route: '/error-monitor',
    label: 'resources.errorMonitor.name',
    icon: ErrorIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
      create: [],
      edit: [],
      delete: [],
      show: [],
    },
    components: {
      list: ErrorMonitor,
      create: undefined,
      edit: undefined,
      show: undefined,
    },
    showInMenu: true,
    menuOrder: 12,
  },

  // ============================================
  // ERROR DEFINITIONS - Error code management (SuperAdmin only)
  // Accessed from Error Monitor, not shown in main menu
  // ============================================
  'error-definitions': {
    name: 'error-definitions',
    route: '/error-definitions',
    label: 'resources.errorDefinitions.name',
    icon: ErrorDefinitionIcon,
    permissions: {
      // Read-only for GroupAdmin/HotelAdmin with the technical capability
      // (proposal §1/§2); create/edit/delete stay Roommatik-only.
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
      create: ['SuperAdmin', 'Admin'],
      edit: ['SuperAdmin', 'Admin'],
      delete: ['SuperAdmin', 'Admin'],
      show: [],
    },
    components: {
      list: ErrorDefinitionList,
      create: undefined,
      edit: undefined,
      show: undefined,
    },
    showInMenu: true, // Técnico rail group, gated by the technical capability (CustomMenu)
    menuOrder: 13,
  },

  // ============================================
  // LOGS - Device Seq logs viewer
  // ============================================
  logs: {
    name: 'logs',
    route: '/logs',
    label: 'resources.logs.name',
    icon: LogsIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
      create: [],
      edit: [],
      delete: [],
      show: [],
    },
    components: {
      list: LogsPage,
      create: undefined,
      edit: undefined,
      show: undefined,
    },
    showInMenu: true,
    menuOrder: 15,
  },

  // Check-in list (check-ins) retired as a customer-facing resource/route
  // (Decision 11): `/check-ins` redirects to `/reservations` (App.tsx), which
  // already carries a `preCheckIn`/`payment`/`key` filter set covering the
  // same ground. `CheckInList` and its dataProvider methods were only
  // consumed here — removed below.

   // ============================================
  // EVENTS - All system events (replaces Operations)
  // ============================================
  'events': {
    name: 'events',
    recordRepresentation: 'reservationNumber',
    route: '/events',
    label: 'resources.events.name',
    icon: OperationIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
    },
    components: {
      list: EventList,
      create: undefined,
      edit: undefined,
      show: undefined,
    },
    showInMenu: true,
    menuOrder: 9,
  },

  // Operations (legacy) retired as a resource/route (F-B-025): `/operations`
  // redirects to `/events` (App.tsx). `OperationList` and its feature folder,
  // i18n file and help config were only consumed here — removed with this
  // resource.

  // Recorridos (journeys) retired as a customer-facing resource/route
  // (Decision 11): `/journeys` redirects to `/reservations` (App.tsx).
  // `JourneyList`/`JourneyShow` and their feature folder were only consumed
  // here — removed with this resource.

  // ============================================
  // DEVICES - Kiosks, dispensers, etc.
  // Customer roles that view hotels (not Receptionist), scoped to their hotels
  // ============================================
  devices: {
    name: 'devices',
    recordRepresentation: 'installationCode',
    route: '/devices',
    label: 'resources.devices.name',
    icon: DeviceIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: ['SuperAdmin', 'Admin'],
      edit: ['SuperAdmin', 'Admin'],
      delete: ['SuperAdmin', 'Admin'],
      show: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      export: ['SuperAdmin', 'Admin'],
    },
    components: {
      list: DeviceList,
      create: DeviceCreate,
      edit: DeviceEdit,
      show: undefined,
    },
    showInMenu: true, // Técnico rail group, gated by the technical capability (CustomMenu)
    menuOrder: 5,
  },

  // ============================================
  // ALERTS - System alerts: read-only for customer roles that view hotels
  // (not Receptionist), scoped to their hotels; changes stay Roommatik-only
  // ============================================
  alerts: {
    name: 'alerts',
    recordRepresentation: 'title',
    route: '/alerts',
    label: 'resources.alerts.name',
    icon: AlertIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: ['SuperAdmin', 'Admin'],
      delete: ['SuperAdmin', 'Admin'],
      show: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      // Same rule as every customer export: Viewer only with «Los lectores pueden exportar».
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
    },
    components: {
      list: AlertList,
      create: undefined,
      edit: undefined,
      show: AlertShow,
    },
    showInMenu: true,
    menuOrder: 14,
  },

  // ============================================
  // HOTELS - Hotel management
  // Hidden from menu - accessed via Admin Options
  // ============================================
  hotels: {
    name: 'hotels',
    recordRepresentation: 'name',
    route: '/hotels',
    label: 'resources.hotels.name',
    icon: HotelIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin'],
      create: ['SuperAdmin', 'Admin'],
      edit: ['SuperAdmin', 'Admin'],
      delete: ['SuperAdmin', 'Admin'],
      show: ['SuperAdmin', 'Admin'],
    },
    components: {
      list: HotelList,
      create: HotelCreate,
      edit: HotelEdit,
      show: undefined,
    },
    showInMenu: false, // Hidden - accessed via Admin Options
    menuOrder: 4,
  },

  // ============================================
  // GROUPS - Group management
  // Hidden from menu - accessed via Admin Options
  // ============================================
  groups: {
    name: 'groups',
    recordRepresentation: 'name',
    route: '/groups',
    label: 'resources.groups.name',
    icon: GroupIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin'],
      list: ['SuperAdmin', 'Admin'],
      create: ['SuperAdmin', 'Admin'],
      edit: ['SuperAdmin', 'Admin'],
      delete: ['SuperAdmin', 'Admin'],
      show: [],
    },
    components: {
      list: GroupList,
      create: GroupCreate,
      edit: GroupEdit,
      show: undefined,
    },
    showInMenu: false, // Hidden - accessed via Admin Options
    menuOrder: 3,
  },

  // ============================================
  // USERS - User management via Keycloak Admin API
  // Hidden from menu - accessed via Admin Options (route stays for deep links)
  // ============================================
  users: {
    name: 'users',
    recordRepresentation: 'name',
    route: '/users',
    label: 'resources.users.name',
    icon: UserIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin'],
      create: ['SuperAdmin', 'Admin', 'GroupAdmin'],
      edit: ['SuperAdmin', 'Admin', 'GroupAdmin'],
      delete: ['SuperAdmin', 'Admin', 'GroupAdmin'],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin'],
    },
    components: {
      list: UserList,
      create: UserCreate,
      edit: UserEdit,
      show: undefined,
    },
    showInMenu: false, // Hidden - accessed via Admin Options
    menuOrder: 2,
  },

  // ============================================
  // ADMIN OPTIONS - Consolidated admin management (SuperAdmin only)
  // ============================================
  'admin-options': {
    name: 'admin-options',
    route: '/admin-options',
    label: 'resources.adminOptions.name',
    icon: AdminOptionsIcon,
    permissions: {
      // Every role can reach this page for the "Mis preferencias" tab
      // (mockup 11: everyone sees it); the other tabs stay independently
      // gated by their own hasResourcePermission() checks inside the page.
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
    },
    components: {
      list: AdminOptionsPage,
      create: undefined,
      edit: undefined,
      show: undefined,
    },
    showInMenu: true,
    menuOrder: 3, // Same position as old groups
  },

  // ============================================
  // RECONCILIATION — Insight v2 (w7.u5, CF S6 "reconciliation, to finance").
  // Property-scoped read, same gate as Operación (CanViewV2PropertyAsync).
  // ============================================
  reconciliation: {
    name: 'reconciliation',
    route: '/reconciliation',
    label: 'resources.reconciliation.name',
    icon: ReconciliationIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
    },
    components: { list: ReconciliationPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 16,
  },

  // ============================================
  // CONTROL — Insight v2 (w7.u5, CF S6 "Control": device health, incidents).
  // Same technical-capability gate as devices/alerts (extended here to the
  // new device/IT-facing v2 page, a judgment call — the backend gate itself
  // is CanViewV2PropertyAsync, same as every other property-scoped screen).
  // ============================================
  control: {
    name: 'control',
    route: '/control',
    label: 'resources.control.name',
    icon: ControlIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
    },
    components: { list: ControlPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 17,
  },

  // ============================================
  // STATUS — Insight v2 (w7.u5, D30/B4: ingest gap register per source).
  // Authenticated, property-scoped variant only — see StatusPage's own
  // comment for the public/anonymous variant CF S6 also asks for, left to
  // Cris. No CSV export: a narrative/per-source shape, not a metric table.
  // ============================================
  status: {
    name: 'status',
    route: '/status',
    label: 'resources.status.name',
    icon: StatusPageIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
    },
    components: { list: StatusPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 18,
  },

  // ============================================
  // BENCHMARK — Insight v2 (w7.u5, CF S6/D22: chain tier, ranking + median
  // marker). Same property-scoped gate; the response itself is chain-wide
  // by construction of the backend's own sibling proxy (w7.u1's own report).
  // ============================================
  benchmark: {
    name: 'benchmark',
    route: '/benchmark',
    label: 'resources.benchmark.name',
    icon: BenchmarkIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      list: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'],
      create: [],
      edit: [],
      delete: [],
      show: [],
      export: ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin'],
    },
    components: { list: BenchmarkPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 19,
  },

  // ============================================
  // FIT — Insight v2 (w7.u5, D16/D25: product C, internal to Roommatik's
  // product team only). Restricted to SuperAdmin/Admin, mirroring the
  // backend's own HasFullAccessAsync gate (V2UiFitController) — never a
  // hotel-facing role, per D25 and CF S6 "Hide: product C".
  // ============================================
  fit: {
    name: 'fit',
    route: '/fit',
    label: 'resources.fit.name',
    icon: FitIcon,
    permissions: {
      access: ['SuperAdmin', 'Admin'],
      list: ['SuperAdmin', 'Admin'],
      create: [],
      edit: [],
      delete: [],
      show: [],
    },
    components: { list: FitPage, create: undefined, edit: undefined, show: undefined },
    showInMenu: true,
    menuOrder: 20,
  },


  // ============================================
// SIMULATORS - Testing simulators
// ============================================
simulators: {
  name: 'simulators',
  route: '/simulators',
  label: 'resources.simulators.name',
  icon: SimulatorIcon,
  permissions: {
    access: ['SuperAdmin', 'Admin'],
    list: ['SuperAdmin', 'Admin'],
    create: [],
    edit: [],
    delete: [],
    show: [],
  },
  components: {
    list: undefined, // Will use custom route
    create: undefined,
    edit: undefined,
    show: undefined,
  },
  showInMenu: true,
  menuOrder: 100,
},
};

// ============================================
// HELPER FUNCTIONS (DEFINED AFTER resourcesConfig)
// ============================================

/**
 * Helper function to check if user has permission for a resource action
 */
export const hasResourcePermission = (
  resourceName: string,
  action: keyof ResourcePermissions
): boolean => {
  try {
    const user = getCurrentUser();
    const currentRole = user?.role || localStorage.getItem('role');
    
    if (!currentRole) {
      console.warn('No role found for permission check');
      return false;
    }
    
    const resourceConfig = resourcesConfig[resourceName];
    if (!resourceConfig) {
      console.warn(`Resource ${resourceName} not found in config`);
      return false;
    }
    
    const permissions = resourceConfig.permissions;
    const allowedRoles = permissions[action];
    
    if (!allowedRoles) {
      console.warn(`Action ${action} not found for resource ${resourceName}`);
      return false;
    }
    
    if (action === 'export') return canExportWith(currentRole, allowedRoles, getCurrentCapabilities());

    const hasPermission = allowedRoles.includes(currentRole as UserRole);
    return hasPermission;
  } catch (error) {
    console.error('Error checking permissions:', error);
    return false;
  }
};

/**
 * Function to get menu items based on permissions
 */
export const getMenuItems = () => {
  const menuItems: any[] = [];
  const user = getCurrentUser();
  const currentRole = user?.role || localStorage.getItem('role');
  
  if (!currentRole) {
    return [];
  }
  
  Object.values(resourcesConfig).forEach((resource: any) => {
    if (resource.showInMenu && hasResourcePermission(resource.name, 'access')) {
      menuItems.push({
        name: resource.name,
        textKey: resource.label,
        icon: resource.icon ? React.createElement(resource.icon) : null,
        route: resource.route,
        order: resource.menuOrder || 99,
      });
    }
  });
  
  // Sort by menuOrder
  return menuItems.sort((a, b) => a.order - b.order);
};

/**
 * Main function to get resources for React Admin
 */
export const getResourcesForReactAdmin = () => {
  const user = getCurrentUser();
  const currentRole = user?.role || localStorage.getItem('role');
  
  if (!currentRole) {
    console.warn('No role found, returning empty resources');
    return [];
  }
  
  // Convert resources to React Admin format
  const resources = Object.values(resourcesConfig)
    .filter((resource: any) => {
      // Only include resources that user has access to
      const hasAccess = hasResourcePermission(resource.name, 'access');
      return hasAccess;
    })
    .map((resource: any) => ({
      name: resource.name,
      list: hasResourcePermission(resource.name, 'list') && resource.components.list
        ? resource.components.list 
        : undefined,
      create: hasResourcePermission(resource.name, 'create') && resource.components.create
        ? resource.components.create 
        : undefined,
      edit: hasResourcePermission(resource.name, 'edit') && resource.components.edit
        ? resource.components.edit 
        : undefined,
      show: hasResourcePermission(resource.name, 'show') && resource.components.show
        ? resource.components.show 
        : undefined,
      options: { label: resource.label },
      recordRepresentation: resource.recordRepresentation,
    }))
    .filter(resource => 
      // Only include resources that have at least one valid component
      resource.list || resource.create || resource.edit || resource.show
    );
  
  return resources;
};

export default resourcesConfig;